import { t } from "@lingui/macro";
import { message } from "antd";
import Title from "antd/lib/typography/Title";
import BarChart from "components/chart/Bar";
import { DATE_FORMAT } from "constants/constants";
import {
	CurrentReportType,
	Report,
	ReportFilterType,
	ReportTotal,
	ReportTotalResponse,
} from "features/dashboard/types";
import moment from "moment";
import { useEffect, useState } from "react";
import { getReport, getTotalsReport } from "service/apis";
import { ReportQuery } from "service/queries";
import { ApiResponse } from "types";
import { MAX_DATE_FILTER, REPORT_TYPE } from "../../../constants";
import { formatPrice } from "utils/format";
import { Datum } from "@ant-design/charts";
import TotalReportSection from "./TotalReportSection";

const defaultCurrentReportType = "date";

const defaultQuery: ReportQuery = {
	reportType: defaultCurrentReportType,
	startDate: moment().subtract(30, "d").format(DATE_FORMAT),
	endDate: moment().format(DATE_FORMAT),
	startMonth: (new Date().getMonth()).toString(),
	endMonth: (new Date().getMonth() + 1).toString(),
	startYear: new Date().getFullYear().toString(),
	endYear: new Date().getFullYear().toString(),
	startQuarter: Math.floor(new Date().getMonth() / 3 ).toString(),
	endQuarter: Math.floor(new Date().getMonth() / 3 + 1 ).toString(),
};

export default function ReportDashboard() {
	const [totalReport, setTotalReport] = useState<ReportTotal>();

	const [customerReport, setCustomerReport] = useState<Report[]>([]);
	const [customerFilter, setCustomerFilter] =
		useState<ReportQuery>(defaultQuery);

	const [revenueReport, setRevenueReport] = useState<Report[]>([]);
	const [revenueFilter, setRevenueFilter] = useState<ReportQuery>(defaultQuery);

	const [merchantReport, setMerchantReport] = useState<Report[]>([]);
	const [merchantFilter, setMerchantFilter] =
		useState<ReportQuery>(defaultQuery);

	const [soldCarReport, setSoldCarReport] = useState<Report[]>([]);
	const [soldCarFilter, setSoldCarFilter] = useState<ReportQuery>(defaultQuery);

	const [carReport, setCarReport] = useState<Report[]>([]);
	const [carFilter, setCarFilter] = useState<ReportQuery>(defaultQuery);

	const [appointmentReport, setAppointmentReport] = useState<Report[]>([]);
	const [appointmentFilter, setAppointmentFilter] = useState<ReportQuery>(defaultQuery);

	const [currentReportType, setCurrentReportType] = useState<CurrentReportType>(
		{
			customer: defaultCurrentReportType,
			merchant: defaultCurrentReportType,
			revenue: defaultCurrentReportType,
			soldCar: defaultCurrentReportType,
			car: defaultCurrentReportType,
			appointment: defaultCurrentReportType,
		},
	);

	useEffect(() => {
		getTotalsReport().then((result: ApiResponse<ReportTotalResponse[]>) => {
			let reports = {};
			result.data.map(report => {
				reports = { ...reports, ...report };
			});
			setTotalReport(reports as ReportTotal);
		});

		getReportCommon(
			REPORT_TYPE.CUSTOMER,
			defaultQuery,
			setCustomerReport,
			"customer",
			stackedCustomerCallback,
		);

		getReportCommon(
			REPORT_TYPE.CAR,
			defaultQuery,
			setCarReport,
			"car",
			stackedCarCallback,
		);

		getReportCommon(
			REPORT_TYPE.APPOINTMENT,
			defaultQuery,
			setAppointmentReport,
			"appointment",
			stackedAppointmentCallback,
		);

		getReportCommon(
			REPORT_TYPE.MERCHANT,
			defaultQuery,
			setMerchantReport,
			"merchant",
			stackedMerchantCallback,
		);
		getReportCommon(
			REPORT_TYPE.REVENUE,
			defaultQuery,
			setRevenueReport,
			"revenue",
		);
		getReportCommon(
			REPORT_TYPE.SOLDCAR,
			defaultQuery,
			setSoldCarReport,
			"soldCar",
		);
	}, []);

	useEffect(() => {
		const statusSections = document.getElementsByClassName("header-status-section");
		let maxHeight = 0;

		for (let index = 0; index < statusSections.length; index ++) {
			const height = (statusSections[index] as HTMLElement).offsetHeight;
			maxHeight = maxHeight < height ? height : maxHeight;
		}

		for (let index = 0; index < statusSections.length; index ++) {
			(statusSections[index] as HTMLElement).style.height = `${maxHeight}px`;
		}
		
	}, []);

	const getReportCommon = (
		type: string,
		query: ReportQuery,
		callback: Function,
		objectType: string,
		stackedCallback?: (data: Report[], type: ReportFilterType) => void,
	) => {
		if (!isValidRangeDate(objectType)) {
			message.error(t`Range dates must not exceed 30 dates`);
			return;
		}

		getReport(type, query).then((result: ApiResponse<Report[]>) => {
			if (stackedCallback) {
				const data = stackedCallback(result.data, query.reportType);
				callback(data);
				setCurrentReportType({
					...currentReportType,
					[objectType]: query.reportType,
				});
			} else {
				const data = result.data.map(value => {
					if (typeof value.total === "string")
						value.total = Number.parseInt(value.total);
					if (typeof value?.totalEnabled === "string")
						value.totalEnabled = Number.parseInt(value.totalEnabled);
					if (value.date) {
						value.date = value.date.split("T")[0];
					}
					return value;
				});

				callback(data);
				setCurrentReportType({
					...currentReportType,
					[objectType]: query.reportType,
				});
			}
		});
	};

	const toNumber = (value?: string | number): number => {
		if (!value) return 0;
		if (typeof value === "number") return value;

		try {
			return Number.parseInt(value);
		} catch (_) {
			return 0;
		}
	};

	const stackedMerchantCallback = (data: Report[], type: ReportFilterType): Report[] => {
		if (data && Array.isArray(data)) {
			const result: Report[] = [];

			for (const d of data) {
				if (
					d.totalUnpaid === "0" &&
					d.totalDisabled === "0" &&
					d.totalEnabled === "0"
				)
					continue;
				if (d.date) d.date = d.date.split("T")[0];

				result.push({
					[type]: d[type],
					value: toNumber(d.totalUnpaid),
					type: t`Unpaid`,
				});
				result.push({
					[type]: d[type],
					value: toNumber(d.totalDisabled),
					type: t`Disabled`,
				});
				result.push({
					[type]: d[type],
					value: toNumber(d.totalEnabled),
					type: t`Enabled`,
				});
			}

			return result;
		}
		return [];
	};

	const stackedCustomerCallback = (data: Report[], type: ReportFilterType): Report[] => {
		if (data && Array.isArray(data)) {
			const result = [];
			for (const d of data) {
				if (
					d.totalDisabled === "0" &&
					d.totalEnabled === "0"
				)
					continue;
				if (d.date) d.date = d.date.split("T")[0];

				result.push({
					[type]: d[type],
					value: toNumber(d.totalDisabled),
					type: t`Disabled`,
				});
				result.push({
					[type]: d[type],
					value: toNumber(d.totalEnabled),
					type: t`Enabled`,
				});
			}
			return result;
		}
		return [];
	};

	const stackedCarCallback = (data: Report[], type: ReportFilterType): Report[] => {
		if (data && Array.isArray(data)) {
			const result = [];
			for (const d of data) {
				if (
					d.totalSoldByMerchant === "0" &&
					d.totalPurchasing === "0" &&
					d.totalUnpublish === "0" &&
					d.totalPublish === "0"
				)
					continue;
				if (d.date) d.date = d.date.split("T")[0];
				result.push({
					[type]: d[type],
					value: toNumber(d.totalSoldByMerchant),
					type: t`Sold by merchant`,
				});
				result.push({
					[type]: d[type],
					value: toNumber(d.totalPurchasing),
					type: t`Purchasing`,
				});
				result.push({
					[type]: d[type],
					value: toNumber(d.totalUnpublish),
					type: t`Unpublish`,
				});
				result.push({
					[type]: d[type],
					value: toNumber(d.totalPublish),
					type: t`Publish`,
				});
			}
			return result;
		}
		return [];
	};

	const stackedAppointmentCallback = (data: Report[], type: ReportFilterType): Report[] => {
		if (data && Array.isArray(data)) {
			const result = [];
			for (const d of data) {
				if (
					d.totalComplete === "0" &&
					d.totalProcessing === "0" &&
					d.totalPending === "0"
				)
					continue;
				if (d.date) d.date = d.date.split("T")[0];
				result.push({
					[type]: d[type],
					value: toNumber(d.totalComplete),
					type: t`Complete`,
				});
				result.push({
					[type]: d[type],
					value: toNumber(d.totalProcessing),
					type: t`Processing`,
				});
				result.push({
					[type]: d[type],
					value: toNumber(d.totalPending),
					type: t`Pending`,
				});
			}
			return result;
		}
		return [];
	};

	const isValidRangeDate = (objectType: string) => {
		let filter: ReportQuery;
		switch (objectType) {
		case "customer":
			filter = customerFilter;
			break;
		case "merchant":
			filter = merchantFilter;
			break;
		case "soldCar":
			filter = soldCarFilter;
			break;
		case "revenue":
			filter = revenueFilter;
			break;
		case "car":
			filter = carFilter;
			break;
		case "appointment":
			filter = appointmentFilter;
			break;
		default:
			return false;
		}
		if (filter) {
			switch (filter.reportType) {
			case "date": {
				const startDate = moment(filter.startDate);
				const endDate = moment(filter.endDate);
				const diff = endDate.diff(startDate, "days");
				return diff <= MAX_DATE_FILTER;
			}
			default:
				return true;
			}
		}
	};

	return (
		<div className="bg-white p-5 h-full my-5 fade-in">
			<Title level={3}>{t`Overview`}</Title>

			<TotalReportSection {...totalReport} />

			<div className="mt-10 flex flex-wrap">
				<BarChart
					className="w-1/2 p-5"
					title={t`Merchants`}
					data={merchantReport}
					xField={currentReportType.merchant}
					yField={"value"}
					filter={merchantFilter}
					updateFilter={setMerchantFilter}
					updateReport={() => {
						getReportCommon(
							REPORT_TYPE.MERCHANT,
							merchantFilter,
							setMerchantReport,
							"merchant",
							stackedMerchantCallback,
						);
					}}
					alias={"merchants"}
					customConfig={{
						colorField: "type",
						color: ["#0D6ABF", "#B3B3B3", "#EB895F"].reverse(),
						tooltip: {
							reserved: true,
						}
					}}
					statusList={ ["Enabled", "Disabled", "Unpaid"] }
				/>

				<BarChart
					className="w-1/2 p-5"
					title={t`Customers`}
					data={customerReport}
					xField={currentReportType.customer}
					yField={"value"}
					filter={customerFilter}
					updateFilter={setCustomerFilter}
					updateReport={() => {
						getReportCommon(
							REPORT_TYPE.CUSTOMER,
							customerFilter,
							setCustomerReport,
							"customer",
							stackedCustomerCallback,
						);
					}}
					customConfig={{
						colorField: "type",
						color: ["#0D6ABF", "#B3B3B3"].reverse()
					}}
					alias={"customers"}
					statusList={ ["Enabled", "Disabled"] }
				/>

				<BarChart
					className="w-1/2 p-5"
					title={t`Cars`}
					data={carReport}
					xField={currentReportType.car}
					yField={"value"}
					filter={carFilter}
					updateFilter={setCarFilter}
					updateReport={() => {
						getReportCommon(
							REPORT_TYPE.CAR,
							carFilter,
							setCarReport,
							"car",
							stackedCarCallback,
						);
					}}
					customConfig={{
						colorField: "type",
						color: ["#0D6ABF", "#B3B3B3", "#EB895F", "#E8D166"].reverse()
					}}
					alias={"cars"}
					statusList={ ["Publish", "Unpublish", "Purchasing", "Sold by merchant" ] }
				/>

				<BarChart
					className="w-1/2 p-5"
					title={t`Appointments`}
					data={appointmentReport}
					xField={currentReportType.appointment}
					yField={"value"}
					filter={appointmentFilter}
					updateFilter={setAppointmentFilter}
					updateReport={() => {
						getReportCommon(
							REPORT_TYPE.APPOINTMENT,
							appointmentFilter,
							setAppointmentReport,
							"appointment",
							stackedAppointmentCallback,
						);
					}}
					customConfig={{
						colorField: "type",
						color: ["#A0D1FF", "#41A4FF", "#0D6ABF" ].reverse()
					}}
					alias={"cars"}
					statusList={ ["Pending", "Processing", "Complete" ] }
				/>

				<BarChart
					className="w-1/2 p-5"
					title={t`Total revenue`}
					data={revenueReport}
					xField={currentReportType.revenue}
					yField={"total"}
					filter={revenueFilter}
					updateFilter={setRevenueFilter}
					updateReport={() => {
						getReportCommon(
							REPORT_TYPE.REVENUE,
							revenueFilter,
							setRevenueReport,
							"revenue",
						);
					}}
					customConfig={{
						columnStyle: {
							fill: "#4b7bec",
							radius: [15, 15, 0, 0],
						},
						tooltip: {
							formatter: (datum: Datum) => ({
								name: t`revenue`,
								value: formatPrice(datum.total),
							}),
						},
					}}
					alias={"revenue"}
				/>

				<BarChart
					className="w-1/2 p-5"
					title={t`Total number of sold cars`}
					data={soldCarReport}
					xField={currentReportType.soldCar}
					yField={"total"}
					filter={soldCarFilter}
					updateFilter={setSoldCarFilter}
					updateReport={() => {
						getReportCommon(
							REPORT_TYPE.SOLDCAR,
							soldCarFilter,
							setSoldCarReport,
							"soldCar",
						);
					}}
					customConfig={{
						columnStyle: {
							fill: "#0fb9b1",
							radius: [15, 15, 0, 0],
						},
					}}
					alias={t`sold cars`}
				/>
			</div>
		</div>
	);
}
